import { useMutation } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useState } from 'react'

import { exportReport } from '@/api/reports-api'
import type { ExportFormat, ReportExportQuery } from '@/api/reports-types'
import { PermissionGate } from '@/components/permission-gate'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { saveBlobAsFile } from '@/lib/download'
import { resolveErrorMessage } from '@/lib/error-messages'

import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'

const FORMATS: ExportFormat[] = ['Csv', 'Xlsx', 'Pdf']

const FORMAT_LABEL: Record<ExportFormat, string> = {
  Csv: 'CSV',
  Xlsx: 'Excel',
  Pdf: 'PDF',
}

/**
 * The most rows one file may carry — the server's `ReportPaging.MaxBulkPageSize`, mirrored so the
 * request the bulk toggle sends is one the server accepts rather than one it clips.
 */
const MAX_BULK_ROWS = 5_000

/**
 * Downloads a report as CSV, Excel or a print-ready PDF — the page on screen by default, or the
 * whole result for a holder of `reports.export.bulk` who ticks the box that says so.
 *
 * Three buttons rather than a format picker, because a picker adds a step to what is, for every
 * format, the same one click a reader already knows: download this page in this shape. Gated on
 * `reports.export` in addition to whatever view permission put the screen's own data on the page —
 * exporting is not implied by reading. `query` is `null` while the screen has nothing loaded yet to
 * export — a filter not yet run, a page still fetching — and the buttons do not render rather than
 * offering to download an empty file.
 *
 * The bulk toggle is one request for one file, never pages stitched client-side: the server checks
 * `reports.export.bulk` against the requested size before a row is built, and a client that
 * assembled the same file from screenfuls would be a copy of the customer base leaving without the
 * permission that exists to notice it. `totalRows` drives the label and the over-cap warning; where
 * a screen cannot cheaply know it, the toggle still works and asks for the cap.
 */
export function ReportExportButtons({
  query,
  totalRows,
}: {
  query: Omit<ReportExportQuery, 'format'> | null
  /** How many rows the whole result holds, where the screen knows. Enables the bulk label. */
  totalRows?: number
}) {
  const permissions = useReportsPermissions()
  const [everything, setEverything] = useState(false)

  const mutation = useMutation({
    mutationFn: (toExport: ReportExportQuery & { format: ExportFormat }) => exportReport(toExport),
    onSuccess: ({ blob, filename }) => {
      saveBlobAsFile(blob, filename)
    },
  })

  if (query === null) {
    return null
  }

  const overCap = (totalRows ?? 0) > MAX_BULK_ROWS
  const bulkQuery = everything ? { ...query, page: 1, pageSize: MAX_BULK_ROWS } : query

  return (
    <PermissionGate permission={REPORTS_PERMISSIONS.export}>
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          {FORMATS.map((format) => (
            <Button
              key={format}
              variant="secondary"
              size="sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ ...bulkQuery, format })}
            >
              <Download />
              {mutation.isPending && mutation.variables?.format === format
                ? 'Exporting…'
                : `Export ${FORMAT_LABEL[format]}`}
            </Button>
          ))}
          {permissions.canExportBulk ? (
            <label className="flex items-center gap-2 text-xs text-body max-lg:min-h-11">
              <Checkbox checked={everything} onCheckedChange={(next) => setEverything(next === true)} />
              {totalRows !== undefined
                ? `Everything (${String(Math.min(totalRows, MAX_BULK_ROWS))} rows), not just this page`
                : 'Everything, not just this page'}
            </label>
          ) : null}
          {mutation.isError ? (
            <span className="text-xs text-danger-text">{resolveErrorMessage(mutation.error)}</span>
          ) : null}
        </div>
        {everything && overCap ? (
          <p className="text-2xs text-warning-text">
            One file carries at most {MAX_BULK_ROWS.toLocaleString('en-GB')} rows; this result holds{' '}
            {(totalRows ?? 0).toLocaleString('en-GB')}. The file will say &ldquo;page 1&rdquo; of
            several — narrow the filters, or take the remainder from the later pages.
          </p>
        ) : null}
      </div>
    </PermissionGate>
  )
}
